import type { AlternativeMode } from '@pomoshnik/shared';

/**
 * Секция «Альтернативы» на DecorCard (Экран 2.2.7) — 4 mood-кнопки в
 * **вертикальном списке** (P-019 MUST). Дизайн-система v0.2.2: каждая кнопка
 * имеет иконку слева в квадрате 36×36 (`#f0e8de`), текст-label, ChevronRight.
 *
 * Иконки — Unicode glyphs вместо lucide-react: проще, без зависимости.
 * Цветные семантические акценты для warmer/cooler разрешены design-system v0.2.2
 * (Flame `#d97706`, Snowflake `#2563eb`).
 *
 * `enabled=false` (B-022 SKU без `dominantLab`) → кнопки disabled с явным
 * reason-text. Видна функциональность, ясна причина недоступности (Phase 7
 * решение #2 — option B вместо скрытия).
 */
export interface AlternativesSectionProps {
  enabled: boolean;
  onSelect: (mode: AlternativeMode) => void;
}

interface MoodOption {
  mode: AlternativeMode;
  label: string;
  /** Decorative Unicode glyph; aria-hidden — semantic info в label. */
  icon: string;
  /** Опциональный цветной акцент для иконки (warmer / cooler). */
  iconColor?: string;
}

const MOODS: readonly MoodOption[] = [
  { mode: 'lighter', label: 'Светлее', icon: '☀' },
  { mode: 'darker', label: 'Темнее', icon: '🌙' },
  { mode: 'warmer', label: 'Теплее', icon: '🔥', iconColor: '#d97706' },
  { mode: 'cooler', label: 'Холоднее', icon: '❄', iconColor: '#2563eb' },
] as const;

export function AlternativesSection({ enabled, onSelect }: AlternativesSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      {!enabled && (
        <p
          role="status"
          className="text-muted text-base m-0 px-1 leading-normal"
          aria-live="polite"
        >
          Нет образца цвета — альтернативы недоступны
        </p>
      )}
      {MOODS.map((m) => (
        <button
          key={m.mode}
          type="button"
          aria-label={m.label}
          aria-disabled={!enabled}
          disabled={!enabled}
          onClick={() => enabled && onSelect(m.mode)}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none',
            enabled ? 'active:opacity-90 cursor-pointer' : 'opacity-40 cursor-not-allowed',
          ].join(' ')}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-icon-bg shrink-0"
            aria-hidden="true"
            style={m.iconColor ? { color: m.iconColor } : undefined}
          >
            {m.icon}
          </span>
          <span className="flex-1 text-left">{m.label}</span>
          <span aria-hidden="true" className="text-muted">
            ›
          </span>
        </button>
      ))}
    </div>
  );
}
